import type { PortalSiteBootstrapResponseDto } from '@supply-mover-2-0/shared';
import { fetchOrNetworkError } from '../lib/portalNetworkError';
import { getPortalTenantHeader } from '../lib/tenantResolver';

/**
 * Portal Site bootstrap service.
 *
 * Calls the public `GET /api/portal/site` endpoint on crm-api to resolve
 * which brand the current hostname maps to. The endpoint is intentionally
 * unauthenticated because it has to run before login.
 *
 * Returns null when the backend replies with 404 — the tenant/brand is
 * unconfigured (opt-in model), so the app should render a not-found state.
 */
const PORTAL_SITE_URL = `${import.meta.env.VITE_API_URL}/api/portal/site`;

export interface PortalSiteBootstrapResult {
	site: PortalSiteBootstrapResponseDto | null;
	notConfigured: boolean;
}

export async function fetchPortalSite(): Promise<PortalSiteBootstrapResult> {
	// The browser's Origin header carries the hostname naturally in prod;
	// in dev the Vite proxy rewrites it, so we forward the current
	// hostname explicitly via X-Portal-Tenant so the backend's middleware
	// can still resolve tenant + brand.
	const headers: Record<string, string> = {};
	const tenantHeader = getPortalTenantHeader();
	if (tenantHeader != null) {
		headers['X-Portal-Tenant'] = tenantHeader;
	}

	// PD-17406 — a blocked request rejects with a PortalNetworkError, which
	// TenantGate shows as a connection problem rather than "Page not found".
	const response = await fetchOrNetworkError(
		PORTAL_SITE_URL,
		{ headers },
		'crm-api',
	);

	if (response.status === 404) {
		return { site: null, notConfigured: true };
	}

	if (!response.ok) {
		throw new Error(
			`Portal site bootstrap failed with status ${response.status}`,
		);
	}

	const body = (await response.json()) as {
		success: boolean;
		data?: PortalSiteBootstrapResponseDto;
	};

	if (!body.success || body.data == null) {
		return { site: null, notConfigured: true };
	}

	return { site: body.data, notConfigured: false };
}

/**
 * PD-17144 — where a texted short link (`/s/:code` on this storefront)
 * leads. Returns null when the link is unknown or expired (the API answers
 * 404 for both, and for a link that belongs to a different host).
 */
export async function resolveShortLink(code: string): Promise<string | null> {
	const headers: Record<string, string> = {};
	const tenantHeader = getPortalTenantHeader();
	if (tenantHeader != null) {
		headers['X-Portal-Tenant'] = tenantHeader;
	}

	const response = await fetchOrNetworkError(
		`${PORTAL_SITE_URL}/short-links/${encodeURIComponent(code)}`,
		{ headers },
		'crm-api',
	);

	if (response.status === 404) {
		return null;
	}

	if (!response.ok) {
		throw new Error(`Short link lookup failed with status ${response.status}`);
	}

	const body = (await response.json()) as { url?: unknown };
	return typeof body.url === 'string' ? body.url : null;
}
